import clsx from "clsx";
import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";

import { useNavigateToLink } from "../hooks/useNavigateToLink";

import { useReducedMotion } from "./ui/use-reduced-motion";

interface FinalCTAProps {
  text: string;
  subtext: string;
  primaryButton: {
    text: string;
    route: string;
    subtext?: string;
  };
  secondaryButton?: {
    text: string;
    route: string;
  };
  leftAlignedLg?: boolean;
  headingLevel?: "h1" | "h2";
}

export function FinalCTA({
  text,
  subtext,
  primaryButton,
  secondaryButton,
  leftAlignedLg,
  headingLevel = "h2",
}: FinalCTAProps) {
  const prefersReducedMotion = useReducedMotion();
  const navigateToLink = useNavigateToLink();
  const Heading = headingLevel === "h1" ? motion.h1 : motion.h2;

  return (
    <div
      className={clsx(
        "relative text-center",
        leftAlignedLg
          ? "lg:text-left"
          : "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-16",
      )}
    >
      <Heading
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
        className="mb-6 leading-[0.95] tracking-tight text-gray-900 dark:text-white"
        style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)", fontWeight: "600" }}
      >
        {text}
      </Heading>

      <motion.p
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={
          prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.1 }
        }
        className="mb-12 leading-relaxed text-gray-600 dark:text-gray-300"
        style={{ fontSize: "clamp(1.125rem, 2.5vw, 1.5rem)" }}
      >
        {subtext}
      </motion.p>

      <motion.div
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={
          prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.2 }
        }
        className={clsx(
          "flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-start",
          leftAlignedLg ? "lg:justify-start" : "",
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={(e) => navigateToLink(e, primaryButton.route)}
            className="group inline-flex cursor-pointer items-center gap-3 rounded-full px-8 py-4 text-white shadow-lg transition-all hover:shadow-xl"
            style={{ backgroundColor: "#0247FE" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#0139D1")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#0247FE")
            }
          >
            <span className="text-lg">{primaryButton.text}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>

          {primaryButton.subtext && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {primaryButton.subtext}
            </p>
          )}
        </div>

        {secondaryButton && (
          <button
            onClick={(e) => navigateToLink(e, secondaryButton.route)}
            className="inline-flex cursor-pointer items-center rounded-full bg-white px-8 py-4 text-gray-700 transition-all hover:bg-gray-50 hover:shadow-lg dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
          >
            <span className="text-lg">{secondaryButton.text}</span>
          </button>
        )}
      </motion.div>
    </div>
  );
}

export function FinalCTASection(props: FinalCTAProps) {
  return (
    <section className="relative w-full py-16 lg:py-32">
      <FinalCTA {...props} />
    </section>
  );
}
